// UK Address Lookup Component - Dropdown-based selection (Ideal Postcodes style)
// Flow: Auto-loads addresses for postcode → Select from dropdown → Fields populate → Edit if needed
import { useState, useRef, useEffect, useMemo, type ReactNode } from 'react'
import { MapPin, Loader2, ChevronDown, Search } from 'lucide-react'
import clsx from 'clsx'
import { useAddressLookup, type UKAddress } from '../hooks/useAddressLookup'
import { usePostcodeSuggestions } from '../hooks/usePostcodeSuggestions'
import { normalizePostcode } from '../services/postcodes'

export interface DeliveryAddress {
  line_1: string
  line_2: string
  line_3: string
  post_town: string
  postcode: string
}

interface UKAddressLookupProps {
  // Postcode the address list is loaded for: the working postcode, or the
  // delivery postcode once it is confirmed real. Empty = no list.
  postcode: string
  value: DeliveryAddress
  onChange: (address: DeliveryAddress) => void
  disabled?: boolean
  errors?: {
    line_1?: string
    post_town?: string
    postcode?: string
  }
  isPostcodeChecking?: boolean
  // Notices shown under the postcode field (e.g. couldn't check it right now)
  postcodeHelp?: ReactNode
}

export function UKAddressLookup({
  postcode,
  value,
  onChange,
  disabled = false,
  errors,
  isPostcodeChecking = false,
  postcodeHelp,
}: UKAddressLookupProps) {
  const { lookup, reset, data, isLoading, error: lookupError } = useAddressLookup()
  const [showDropdown, setShowDropdown] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [showPostcodeSuggestions, setShowPostcodeSuggestions] = useState(false)
  const postcodeSuggestionsRef = useRef<HTMLDivElement>(null)
  const postcodeInputRef = useRef<HTMLInputElement>(null)
  const selectPostcodeSuggestion = (suggestion: string) => {
    setShowPostcodeSuggestions(false)
    handleFieldChange('postcode', suggestion)
  }
  // Close the list when focus leaves both the field and the list. Focus
  // moving between them (e.g. Tab onto a suggestion) keeps it open, so
  // keyboard users can reach the suggestions.
  const closePostcodeSuggestionsOnBlur = (event: React.FocusEvent) => {
    const next = event.relatedTarget as Node | null
    if (next && (postcodeSuggestionsRef.current?.contains(next) || postcodeInputRef.current === next)) {
      return
    }
    // Deferred so a tap on a suggestion (which blurs the input) still
    // registers before the list closes.
    window.setTimeout(() => setShowPostcodeSuggestions(false), 100)
  }
  // Hide the postcode already in the box: picking it would change nothing.
  const postcodeSuggestions = usePostcodeSuggestions(
    value.postcode,
    showPostcodeSuggestions && !disabled,
  ).filter((suggestion) => normalizePostcode(suggestion) !== normalizePostcode(value.postcode))
  // Close the dropdown and clear its search when the list's postcode changes
  // (adjusting state during render, as React recommends, not in an effect).
  const [listPostcode, setListPostcode] = useState(postcode)
  if (listPostcode !== postcode) {
    setListPostcode(postcode)
    setShowDropdown(false)
    setSearchQuery('')
  }
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
        setSearchQuery('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Load the address list whenever its postcode changes.
  useEffect(() => {
    if (postcode) {
      lookup(postcode)
    } else {
      reset()
    }
  }, [postcode, lookup, reset])

  // Handle address selection from dropdown
  const handleSelectAddress = (addr: UKAddress) => {
    setShowDropdown(false)
    setSearchQuery('')
    onChange({
      line_1: addr.line_1 || '',
      line_2: addr.line_2 || '',
      line_3: addr.line_3 || '',
      post_town: addr.post_town || '',
      postcode: addr.postcode || postcode,
    })
  }

  // Handle field changes (manual editing)
  const handleFieldChange = (field: keyof DeliveryAddress, fieldValue: string) => {
    onChange({
      ...value,
      [field]: fieldValue,
    })
  }

  // Format address for dropdown display
  const formatAddressDisplay = (addr: UKAddress): string => {
    const parts = [
      addr.sub_building_name,
      addr.building_name,
      addr.line_1,
      addr.line_2,
      addr.line_3,
    ].filter(Boolean)
    return parts.join(', ')
  }

  // Filter addresses based on search query
  const filteredAddresses = useMemo(() => {
    if (!data?.addresses) return []
    if (!searchQuery.trim()) return data.addresses

    const query = searchQuery.toLowerCase()
    return data.addresses.filter((addr) => {
      const addressText = formatAddressDisplay(addr).toLowerCase()
      return addressText.includes(query)
    })
  }, [data?.addresses, searchQuery])

  return (
    <div className="space-y-4">
      {/* Address Selector - Dropdown with Find Address button */}
      <div ref={dropdownRef} className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Please select your address
        </label>

        {/* Combobox input - search and select in one */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setShowDropdown(true)
            }}
            onFocus={() => {
              if (data?.addresses && data.addresses.length > 0) {
                setShowDropdown(true)
              }
            }}
            placeholder={
              isLoading
                ? 'Loading addresses...'
                : isPostcodeChecking
                  ? 'Checking postcode...'
                  : data?.addresses?.length
                    ? 'Search or select an address...'
                    : 'No addresses found - enter manually below'
            }
            disabled={disabled || isLoading}
            className={clsx(
              'w-full h-14 pl-12 pr-10 text-base bg-white border-2 rounded-xl',
              'placeholder:text-gray-400',
              'focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary',
              'transition-all duration-200',
              disabled || isLoading ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300 hover:border-gray-400'
            )}
          />
          {isLoading ? (
            <div className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
              <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
            </div>
          ) : data?.addresses?.length ? (
            <ChevronDown className={clsx(
              'absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 transition-transform pointer-events-none',
              showDropdown && 'rotate-180'
            )} />
          ) : null}
        </div>

        {/* Dropdown menu */}
        {showDropdown && data?.addresses && data.addresses.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-2 z-20 bg-white border-2 border-gray-200 rounded-xl shadow-lg overflow-hidden">
            {/* Address list */}
            <div className="max-h-[280px] overflow-y-auto divide-y divide-gray-100">
              {filteredAddresses.length > 0 ? (
                filteredAddresses.map((addr, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSelectAddress(addr)}
                    className="w-full text-left px-4 py-3.5 min-h-[48px] hover:bg-gray-50 active:bg-gray-100 transition-colors flex items-center gap-3"
                  >
                    <MapPin className="w-5 h-5 text-brand-primary flex-shrink-0" />
                    <p className="text-sm text-gray-900 font-medium leading-snug flex-1">
                      {formatAddressDisplay(addr)}
                    </p>
                  </button>
                ))
              ) : (
                <div className="px-4 py-6 text-center">
                  <p className="text-gray-500 text-sm">No addresses match "{searchQuery}"</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Error message */}
        {lookupError && postcode && (
          <p className="text-sm text-amber-700 mt-2">
            Unable to search addresses. Please enter your address manually below.
          </p>
        )}
      </div>

      {/* Address Line 1 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Address line one <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={value.line_1}
          onChange={(e) => handleFieldChange('line_1', e.target.value)}
          placeholder="e.g. 123 High Street"
          disabled={disabled}
          className={clsx(
            'w-full h-14 px-4 text-base text-gray-900 bg-white border-2 rounded-xl placeholder:text-gray-400',
            'focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary',
            'transition-all duration-200',
            disabled ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300',
            errors?.line_1 && 'border-red-500 focus:ring-red-500 focus:border-red-500'
          )}
        />
        {errors?.line_1 && (
          <p className="text-red-600 text-xs mt-1">{errors.line_1}</p>
        )}
      </div>

      {/* Address Line 2 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Address line two <span className="text-gray-400 text-xs">(optional)</span>
        </label>
        <input
          type="text"
          value={value.line_2}
          onChange={(e) => handleFieldChange('line_2', e.target.value)}
          placeholder="e.g. Flat 4, Building Name"
          disabled={disabled}
          className={clsx(
            'w-full h-14 px-4 text-base text-gray-900 bg-white border-2 rounded-xl placeholder:text-gray-400',
            'focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary',
            'transition-all duration-200',
            disabled ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
          )}
        />
      </div>

      {/* Address Line 3 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Address line three <span className="text-gray-400 text-xs">(optional)</span>
        </label>
        <input
          type="text"
          value={value.line_3}
          onChange={(e) => handleFieldChange('line_3', e.target.value)}
          placeholder="e.g. Industrial Estate"
          disabled={disabled}
          className={clsx(
            'w-full h-14 px-4 text-base text-gray-900 bg-white border-2 rounded-xl placeholder:text-gray-400',
            'focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary',
            'transition-all duration-200',
            disabled ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300'
          )}
        />
      </div>

      {/* Post Town */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Post town <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={value.post_town}
          onChange={(e) => handleFieldChange('post_town', e.target.value)}
          placeholder="e.g. London"
          disabled={disabled}
          className={clsx(
            'w-full h-14 px-4 text-base text-gray-900 bg-white border-2 rounded-xl placeholder:text-gray-400',
            'focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary',
            'transition-all duration-200',
            disabled ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300',
            errors?.post_town && 'border-red-500 focus:ring-red-500 focus:border-red-500'
          )}
        />
        {errors?.post_town && (
          <p className="text-red-600 text-xs mt-1">{errors.post_town}</p>
        )}
      </div>

      {/* Delivery postcode - starts as the working postcode; editing it only
          changes where the goods go, never the working postcode or basket */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Postcode <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="text"
            value={value.postcode}
            onChange={(e) => {
              handleFieldChange('postcode', e.target.value)
              setShowPostcodeSuggestions(true)
            }}
            ref={postcodeInputRef}
            onFocus={() => setShowPostcodeSuggestions(true)}
            onBlur={closePostcodeSuggestionsOnBlur}
            placeholder="e.g. SO16 0AS"
            // Off so the browser's own autofill list doesn't cover ours.
            autoComplete="off"
            autoCapitalize="characters"
            disabled={disabled}
            className={clsx(
              'w-full h-14 px-4 text-base text-gray-900 bg-white border-2 rounded-xl placeholder:text-gray-400 uppercase',
              'focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary',
              'transition-all duration-200',
              disabled ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300',
              errors?.postcode && 'border-red-500 focus:ring-red-500 focus:border-red-500'
            )}
          />
          {postcodeSuggestions.length > 0 && (
            <div
              ref={postcodeSuggestionsRef}
              onBlur={closePostcodeSuggestionsOnBlur}
              className="absolute top-full left-0 right-0 mt-2 z-20 max-h-[280px] overflow-y-auto bg-white border-2 border-gray-200 rounded-xl shadow-lg divide-y divide-gray-100">
              {postcodeSuggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  // onMouseDown (not onClick) so selection fires before the
                  // input's onBlur closes the list.
                  onMouseDown={(event) => {
                    event.preventDefault()
                    selectPostcodeSuggestion(suggestion)
                  }}
                  // Enter/Space on a focused suggestion fires a click with
                  // detail 0; pointer clicks are already handled above.
                  onClick={(event) => {
                    if (event.detail === 0) selectPostcodeSuggestion(suggestion)
                  }}
                  className="w-full text-left px-4 py-3.5 min-h-[48px] hover:bg-gray-50 active:bg-gray-100 transition-colors flex items-center gap-3"
                >
                  <MapPin className="w-5 h-5 text-brand-primary flex-shrink-0" />
                  <span className="text-sm text-gray-900 font-medium">{suggestion}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {errors?.postcode && (
          <p className="text-red-600 text-xs mt-1">{errors.postcode}</p>
        )}
        {postcodeHelp}
      </div>
    </div>
  )
}
